import React from 'react';
import { PageHeader, Badge, Button } from '../../components/ui';
import { useNotifications } from '../../contexts/NotificationContext';
import { formatTimeAgo } from '../../utils/notifications';
import {
  BellIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

const EngineerNotifications: React.FC = () => {
  const {
    notifications,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    refreshNotifications,
    unreadCount,
  } = useNotifications();

  return (
    <div className="max-w-3xl mx-auto px-4 py-4 space-y-4">
      <PageHeader
        title="Notifications"
        subtitle="Assignments, certificate status changes, messages and other updates."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={refreshNotifications} loading={loading}>
              Refresh
            </Button>
            {unreadCount > 0 && (
              <Button variant="secondary" size="sm" onClick={markAllAsRead}>
                Mark all read
              </Button>
            )}
          </div>
        }
      />

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      <div className="space-y-2">
        {notifications.length === 0 && !loading && (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
            <BellIcon className="w-10 h-10 mx-auto text-slate-400" />
            <div className="mt-2 text-sm font-semibold text-slate-700">All caught up</div>
            <div className="text-xs text-slate-500 mt-1">
              New assignments, cert reviews and messages will show here.
            </div>
          </div>
        )}

        {notifications.map((n) => (
          <div
            key={n.id}
            className={`bg-white border rounded-xl p-4 shadow-sm transition-colors ${
              n.read ? 'border-slate-200' : 'border-blue-300 bg-blue-50/30'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
                <BellIcon className="w-5 h-5 text-slate-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-slate-900 truncate">{n.title}</div>
                  <div className="text-xs text-slate-500 shrink-0">
                    {formatTimeAgo(n.createdAt)}
                  </div>
                </div>
                <div className="text-sm text-slate-700 mt-0.5">{n.message}</div>
                <div className="mt-2 flex items-center gap-2">
                  <Badge tone={n.read ? 'neutral' : 'info'}>{n.type.replace(/_/g, ' ')}</Badge>
                  {!n.read && (
                    <button
                      type="button"
                      className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
                      onClick={() => markAsRead(n.id)}
                    >
                      <CheckCircleIcon className="w-4 h-4" />
                      Mark read
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EngineerNotifications;
